import React, { useEffect, useState } from "react";
import { trackEvent } from "@leafly-com/web-utils";
import { GetServerSideProps, NextPage } from "next";
import { useRouter } from "next/router";
import { useSelector } from "react-redux";

import { getDispensary } from "api/requests/getDispensary";
import { postDispensaryReview } from "api/requests/postDispensaryReview";
import { DISPENSARY_PLACEHOLDER_PHOTO } from "constants/dispensary";
import { SnackBarMessages, useSnackBar } from "context/SnackBarContext";
import { Dispensary } from "custom-types/Dispensary";
import useDomainCountryCode from "hooks/useDomainCountryCode";
import useUserFavorites from "redux/hooks/useUserFavorites";
import { getIsLoggedIn, getIsUserLoading } from "redux/selectors/user";
import { getDispensaryUrl } from "utils/dispensaryUrls";
import generateDispensaryRedirect from "utils/generateDispensaryRedirect";
import logError from "utils/logError";
import { getCountryCodeHeader } from "utils/requestContext";
import { trackReviewsEvent } from "utils/trackEvent";

import Button from "components/botanic/Button";
import Checkbox from "components/botanic/Checkbox";
import TextArea from "components/botanic/TextArea";
import ArrowIcon from "components/Icons/arrows/ArrowIcon";
import Spinner from "components/Icons/Spinner";
import Image from "components/Image";
import MedicalDisclaimers from "components/MedicalDisclaimers";
import MetaTags from "components/MetaTags";
import RatingCard from "components/RatingCard";
import SsoBumpoutModal from "components/SsoBumpoutModal";

const STAR_ERROR_MESSAGE = "Please select a star rating";

const REVIEW_DISCLAIMER =
  "All reviews will be verified after submission; please give us time before they show up on the page. Thank you.";

const WriteReviewPage: NextPage<{
  dispensary: Dispensary;
}> = ({ dispensary }) => {
  const userIsLoggedIn = useSelector(getIsLoggedIn);
  const userIsLoading = useSelector(getIsUserLoading);

  const [showSsoModal, setShowSsoModal] = useState(false);
  useEffect(() => {
    if (!userIsLoading && !userIsLoggedIn) {
      trackEvent("Reviews", "Impression", "Sign in prompt");
      setShowSsoModal(true);
    }
  }, [userIsLoading, userIsLoggedIn]);

  const [atmosphereRating, setAtmosphereRating] = useState(null);
  const [qualityRating, setQualityRating] = useState(null);
  const [serviceRating, setServiceRating] = useState(null);
  const [isFirstVisit, setIsFirstVisit] = useState(false);
  const [wouldRecommend, setWouldRecommend] = useState(false);
  const [wouldShopAgain, setWouldShopAgain] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const [text, setText] = useState<string | null>(null);
  const [showBackSpinner, setBackSpinner] = useState(false);
  const [showSubmitSpinner, setSubmitSpinner] = useState(false);
  const [showSubmitError, setShowSubmitError] = useState(false);

  const { addSnackBarItem } = useSnackBar();
  const router = useRouter();

  const { useInitiateFavorites, followedDispensaries } = useUserFavorites();
  useInitiateFavorites();

  const domainCountryCode = useDomainCountryCode();

  // * Safe to deconstruct this now.
  const { logoUrl, name, city, state, slug } = dispensary;

  const returnToDispensaryPage = () => {
    setBackSpinner(true);
    router.push(`/dispensary-info/${slug}`);
  };
  const returnToDispensaryPageWithConfirmation = () => {
    setBackSpinner(true);
    router.push(`/dispensary-info/${slug}?show_confirmation_modal=true`);
  };
  const returnToDispensaryPageWithConfirmationAndMessage = () => {
    setBackSpinner(true);
    router.push(
      `/dispensary-info/${slug}?show_confirmation_modal=true&show_favorite_message=true`,
    );
  };

  const isValidReview = qualityRating && serviceRating && atmosphereRating;

  const ssoDispensaryRedirect = async () => {
    try {
      setSubmitSpinner(true);
      await router.push(
        getDispensaryUrl({
          countryCode: domainCountryCode,
          slug,
        }),
      );
    } catch (err) {
      logError(
        "Error redirecting in ssoDispensaryRedirect in /dispensary-info/[slug]/write-review",
        {
          functionName: "ssoDispensaryRedirect",
        },
      );
    }
  };

  const handleSubmit = async () => {
    trackReviewsEvent("Click", "Review this Dispensary Button");

    if (isValidReview && userIsLoggedIn) {
      setSubmitSpinner(true);
      setShowSubmitError(false);

      const postDispensaryReviewSuccess = await postDispensaryReview(
        slug,
        domainCountryCode,
        {
          atmosphereRating,
          isFirstVisit,
          qualityRating,
          serviceRating,
          text,
          wouldRecommend,
          wouldShopAgain,
        },
      );

      if (postDispensaryReviewSuccess) {
        if (
          // if the review is positive (+3 stars for any category), show "favorite this dispensary" modal
          (atmosphereRating && parseInt(atmosphereRating, 10) >= 3) ||
          (qualityRating && parseInt(qualityRating, 10) >= 3) ||
          (serviceRating && parseInt(serviceRating, 10) >= 3)
        ) {
          if (
            followedDispensaries &&
            followedDispensaries?.dispensaries?.find((d) => d?.slug === slug)
          ) {
            // if the user is already following the dispensary, show "shop this dispensary" modal
            return returnToDispensaryPageWithConfirmation();
          }
          return returnToDispensaryPageWithConfirmationAndMessage();
        } else {
          // if negative review, just redirect (no modal)
          trackEvent("Reviews", "Click", "Negative CTA");
          ssoDispensaryRedirect();
          return addSnackBarItem(SnackBarMessages.dispensaryReviewSubmitted);
        }
      } else {
        setShowSubmitError(true);
        setSubmitSpinner(false);

        return;
      }
    } else {
      setInvalid(true);
    }
  };

  return (
    <>
      <MetaTags
        title={`Write a review for ${name} | Leafly`}
        description={`Review ${name} dispensary on Leafly.`}
        robots={"noindex,noarchive"}
      />
      <div className="container" data-testid="write-review-container">
        <div className="flex flex-start mt-lg">
          <button
            type="button"
            className="text-green text-xs flex items-center mt-lg"
            onClick={returnToDispensaryPage}
          >
            <ArrowIcon width="16" height="16" direction="left" />
            <span className="ml-xs underline font-bold">Back to {name}</span>
          </button>
        </div>
        <div className="pb-xl pt-sm">
          <div className="flex flex-start">
            <div className="w-[60px] h-[60px]">
              <Image
                sizes={[100]}
                alt={name}
                className="rounded"
                src={logoUrl || DISPENSARY_PLACEHOLDER_PHOTO}
                disableLazyLoad
              />
            </div>
            <div className="mx-md pt-sm">
              <h4>{name}</h4>
              <p className="text-xs">
                {city}, {state}
              </p>
            </div>
          </div>
        </div>
        {showBackSpinner && <Spinner />}
        <div className="row">
          <div className="col md:col-1/3">
            <RatingCard
              title="Quality of the products"
              invalid={invalid}
              rating={qualityRating}
              setRating={setQualityRating}
              errorMessage={STAR_ERROR_MESSAGE}
            />
          </div>
          <div className="col md:col-1/3">
            <RatingCard
              invalid={invalid}
              rating={serviceRating}
              setRating={setServiceRating}
              title="Overall service"
              errorMessage={STAR_ERROR_MESSAGE}
            />
          </div>
          <div className="col md:col-1/3">
            <RatingCard
              invalid={invalid}
              rating={atmosphereRating}
              setRating={setAtmosphereRating}
              title="Atmosphere"
              errorMessage={STAR_ERROR_MESSAGE}
            />
          </div>
        </div>
        <hr className="border-light-grey mx-xs mt-lg mb-xl" />
        <h3 className="text-bold text-md">
          Any additional thoughts? (optional)
        </h3>
        <p className="text-sm text-default">
          Your input matters and helps to shape our cannabis community here at
          Leafly.
        </p>
        <TextArea
          label="Share your experience"
          id="review-text-input"
          height="250px"
          width="100%"
          textareaClasses="mb-md bg-leafly-white text-sm text-default border border-light-grey rounded"
          value={text || undefined}
          onChange={(e) => setText(e.currentTarget.value)}
        />
        {invalid && (
          <div className="text-xs text-error mb-sm">
            Share with us your thoughts
          </div>
        )}
        <div className="pb-lg">
          <div className="mb-md">
            <Checkbox
              id="first-visit"
              data-testid="first-visit"
              checked={isFirstVisit}
              onChange={() => setIsFirstVisit(!isFirstVisit)}
            >
              <span>This was my first time here</span>
            </Checkbox>
          </div>
          <div className="mb-md">
            <Checkbox
              id="recommend"
              data-testid="recommend"
              checked={wouldRecommend}
              onChange={() => setWouldRecommend(!wouldRecommend)}
            >
              <span>I’d recommend this place to a friend</span>
            </Checkbox>
          </div>
          <div className="mb-md">
            <Checkbox
              id="shop-again"
              data-testid="shop-again"
              checked={wouldShopAgain}
              onChange={() => setWouldShopAgain(!wouldShopAgain)}
            >
              <span>I’d shop here again</span>
            </Checkbox>
          </div>
        </div>
        <MedicalDisclaimers
          usDisclaimer={REVIEW_DISCLAIMER}
          className="mb-lg"
        />
        <div className="mb-xxl lg:flex lg:flex-row flex flex-col-reverse">
          <button
            onClick={() => {
              ssoDispensaryRedirect();
            }}
            className="underline mr-xxl mt-xl lg:mt-sm"
          >
            cancel
          </button>
          <Button data-testid="write-review" onClick={handleSubmit}>
            submit review
          </Button>
        </div>
        {showSubmitError && (
          <div className="text-error text-md mb-lg">
            Something went wrong, please try again in a moment
          </div>
        )}

        {!userIsLoggedIn && (
          <div className="fixed top-0 right-0 w-full h-full bg-white opacity-75 z-10" />
        )}

        {showSsoModal && (
          <SsoBumpoutModal
            closeModal={() => router.back()}
            title="You need to be logged in to leave a review."
            trackSignIn={() => trackEvent("Reviews", "Click", "Sign in")}
            trackSignUp={() => trackEvent("Reviews", "Click", "Sign up")}
          />
        )}
        {showSubmitSpinner && <Spinner />}
      </div>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const domainCountryCode = getCountryCodeHeader(context.req.headers);

  const dispensary = await getDispensary(context.query.slug as string);

  const redirect = generateDispensaryRedirect(
    domainCountryCode,
    context.resolvedUrl,
    context.query,
    dispensary,
  );

  if (redirect) {
    return { redirect };
  }

  return {
    props: { dispensary },
  };
};

export default WriteReviewPage;
